/**
 * Dashboard Page Component
 * Purpose: User dashboard for managing shortened URLs
 * Author: Traklet App
 * Created: 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  TablePagination,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  useMediaQuery,
  useTheme,
  Divider,
} from '@mui/material';
import {
  ContentCopy,
  Delete,
  Analytics,
  Add,
  Search,
  Link as LinkIcon,
  MoreVert,
  Refresh,
  FilterList,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { urlApi, apiUtils } from '../utils/api';
import type { UrlData, PaginatedUrls } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State for URL management
  const [urls, setUrls] = useState<UrlData[]>([]);
  const [pagination, setPagination] = useState({
    total_count: 0,
    current_page: 1,
    total_pages: 1,
    limit: 10,
    offset: 0,
    has_more: false,
  });
  
  // State for UI
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // State for URL creation
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUrl, setNewUrl] = useState({ longUrl: '', customAlias: '' });
  const [isCreating, setIsCreating] = useState(false);
  
  // State for deletion
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; url: UrlData | null }>({
    open: false,
    url: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // State for menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUrl, setSelectedUrl] = useState<UrlData | null>(null);

  /**
   * Fetch URLs from API
   */
  const fetchUrls = useCallback(async (page = 1) => {
    try {
      setIsLoading(true);
      const offset = (page - 1) * pagination.limit;
      
      const response = await urlApi.getUrls({
        limit: pagination.limit,
        offset,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      
      if (response.success && response.data) {
        setUrls(response.data.urls);
        setPagination(response.data.pagination);
      } else {
        setError(response.message || 'Failed to fetch URLs');
      }
    } catch (error) {
      setError(apiUtils.getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, sortBy, sortOrder, pagination.limit]);

  /**
   * Create new shortened URL
   */
  const handleCreateUrl = async () => {
    if (!newUrl.longUrl.trim()) {
      setError('Please enter a URL to shorten');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const response = await urlApi.shorten(
        newUrl.longUrl.trim(),
        newUrl.customAlias.trim() || undefined
      );
      
      if (response.success) {
        setSuccess('URL shortened successfully!');
        setNewUrl({ longUrl: '', customAlias: '' });
        setShowCreateForm(false);
        fetchUrls(pagination.current_page);
      } else {
        setError(response.message || 'Failed to shorten URL');
      }
    } catch (error) {
      setError(apiUtils.getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  /**
   * Delete URL
   */
  const handleDeleteUrl = async () => {
    if (!deleteDialog.url) return;
    
    setIsDeleting(true);
    
    try {
      const response = await urlApi.deleteUrl(deleteDialog.url.id);
      
      if (response.success) {
        setSuccess('URL deleted successfully!');
        setDeleteDialog({ open: false, url: null });
        fetchUrls(pagination.current_page);
      } else {
        setError(response.message || 'Failed to delete URL');
      }
    } catch (error) {
      setError(apiUtils.getErrorMessage(error));
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Copy URL to clipboard
   */
  const handleCopyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setSuccess('URL copied to clipboard!');
    } catch (error) {
      setError('Failed to copy URL');
    }
  };

  /**
   * Handle menu actions
   */
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, url: UrlData) => {
    setAnchorEl(event.currentTarget);
    setSelectedUrl(url);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUrl(null);
  };

  /**
   * Handle page change
   */
  const handlePageChange = (event: unknown, newPage: number) => {
    fetchUrls(newPage + 1);
  };

  /**
   * Handle search
   */
  const handleSearch = () => {
    fetchUrls(1);
  };

  /**
   * Close notifications
   */
  const handleCloseSnackbar = () => {
    setError('');
    setSuccess('');
  };

  // Load URLs on component mount
  useEffect(() => {
    fetchUrls();
  }, [fetchUrls]);

  if (isLoading && urls.length === 0) {
    return <LoadingSpinner message="Loading your URLs..." />;
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box mb={{ xs: 3, md: 4 }}>
        <Typography 
          variant={isMobile ? "h5" : "h4"} 
          component="h1" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
          }}
        >
          Dashboard
        </Typography>
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
        >
          {isMobile ? 'Manage URLs' : 'Manage your shortened URLs and view analytics'}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} mb={{ xs: 3, md: 4 }}>
        <Grid item xs={6} sm={6} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Typography 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Total URLs
              </Typography>
              <Typography 
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                {pagination.total_count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={4}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 }, '&:last-child': { pb: { xs: 2, sm: 3 } } }}>
              <Typography 
                color="text.secondary" 
                gutterBottom
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Total Clicks
              </Typography>
              <Typography 
                variant={isMobile ? "h5" : "h4"}
                sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem' } }}
              >
                {urls.reduce((sum, url) => sum + url.click_count, 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 2, sm: 3 }, 
          mb: { xs: 2, md: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <Stack spacing={{ xs: 2, md: 3 }}>
          {/* Search and New URL Button */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ sm: 'center' }}
          >
            <TextField
              fullWidth
              size={isMobile ? 'small' : 'medium'}
              placeholder="Search URLs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize={isMobile ? 'small' : 'medium'} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setShowCreateForm(true)}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                minWidth: { xs: '100%', sm: 'auto' },
                whiteSpace: 'nowrap'
              }}
            >
              {isMobile ? 'New' : 'New URL'}
            </Button>
          </Stack>
          
          {/* Sort and Search Button */}
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ sm: 'center' }}
          >
            <FormControl size={isMobile ? 'small' : 'medium'} sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="created_at">Date</MenuItem>
                <MenuItem value="click_count">Clicks</MenuItem>
                <MenuItem value="short_code">Code</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Search />}
              onClick={handleSearch}
              disabled={isLoading}
              size={isMobile ? 'small' : 'medium'}
              sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            >
              Search
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* URLs List - Responsive */}
      {isMobile ? (
        // Mobile Card Layout
        <Stack spacing={2}>
          {urls.map((url) => (
            <Card 
              key={url.id} 
              elevation={0} 
              sx={{ 
                border: '1px solid', 
                borderColor: 'divider', 
                borderRadius: 2 
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack spacing={2}>
                  {/* Short URL and Clicks */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                      <LinkIcon fontSize="small" color="primary" />
                      <Typography
                        component="a"
                        href={url.short_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          '&:hover': { textDecoration: 'underline' },
                        }}
                      >
                        {url.short_code}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${url.click_count} clicks`}
                      color={url.click_count > 0 ? 'primary' : 'default'}
                      size="small"
                    />
                  </Stack>
                  
                  {/* Long URL */}
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '0.75rem'
                    }}
                    title={url.long_url}
                  >
                    {url.long_url}
                  </Typography>
                  
                  {/* Date */}
                  <Typography variant="caption" color="text.secondary">
                    {new Date(url.created_at).toLocaleDateString()}
                  </Typography>
                  
                  <Divider />
                  
                  {/* Actions */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      size="small"
                      startIcon={<Analytics />}
                      onClick={() => navigate(`/analytics/${url.id}`)}
                      sx={{ flex: 1 }}
                    >
                      Analytics
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={() => handleCopyUrl(url.short_url)}
                      variant="outlined"
                      sx={{ flex: 1 }}
                    >
                      Copy
                    </Button>
                    <IconButton
                      onClick={(e) => handleMenuOpen(e, url)}
                      size="small"
                    >
                      <MoreVert />
                    </IconButton>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        // Desktop Table Layout
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Short URL</TableCell>
                  <TableCell>Long URL</TableCell>
                  <TableCell align="center">Clicks</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {urls.map((url) => (
                  <TableRow key={url.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <LinkIcon fontSize="small" color="primary" />
                        <Typography
                          component="a"
                          href={url.short_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            fontWeight: 500,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {url.short_code}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          maxWidth: isTablet ? 200 : 300,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={url.long_url}
                      >
                        {url.long_url}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={url.click_count}
                        color={url.click_count > 0 ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(url.created_at).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="View Analytics">
                          <IconButton
                            onClick={() => navigate(`/analytics/${url.id}`)}
                            size="small"
                            color="primary"
                          >
                            <Analytics />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy URL">
                          <IconButton
                            onClick={() => handleCopyUrl(url.short_url)}
                            size="small"
                          >
                            <ContentCopy />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, url)}
                          size="small"
                        >
                          <MoreVert />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      
      {/* Pagination */}
      <Box mt={2}>
        <TablePagination
          component="div"
          count={pagination.total_count}
          page={pagination.current_page - 1}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.limit}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{
            '& .MuiTablePagination-toolbar': {
              px: { xs: 1, sm: 2 },
              minHeight: { xs: 52, sm: 64 }
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (selectedUrl) handleCopyUrl(selectedUrl.short_url);
            handleMenuClose();
          }}
        >
          <ContentCopy sx={{ mr: 1 }} />
          Copy URL
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedUrl) navigate(`/analytics/${selectedUrl.id}`);
            handleMenuClose();
          }}
        >
          <Analytics sx={{ mr: 1 }} />
          View Analytics
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialog({ open: true, url: selectedUrl });
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <Delete sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Create URL Dialog */}
      <Dialog open={showCreateForm} onClose={() => setShowCreateForm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Short URL</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Long URL"
            placeholder="https://example.com/very/long/url"
            fullWidth
            variant="outlined"
            value={newUrl.longUrl}
            onChange={(e) => setNewUrl(prev => ({ ...prev, longUrl: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Custom Alias (Optional)"
            placeholder="my-custom-link"
            fullWidth
            variant="outlined"
            value={newUrl.customAlias}
            onChange={(e) => setNewUrl(prev => ({ ...prev, customAlias: e.target.value }))}
            helperText="Leave empty for random alias"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateForm(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUrl}
            variant="contained"
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, url: null })}>
        <DialogTitle>Delete URL</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this URL? This action cannot be undone.
          </Typography>
          {deleteDialog.url && (
            <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="text.secondary">
                Short URL: {deleteDialog.url.short_url}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Long URL: {deleteDialog.url.long_url}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, url: null })}>Cancel</Button>
          <Button
            onClick={handleDeleteUrl}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error || !!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? 'error' : 'success'}
          variant="filled"
        >
          {error || success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Dashboard;